import { bech32 } from 'bech32';
import web3 from 'web3';
import { AbiItem } from 'web3-utils';
import Decimal from 'decimal.js-light';
import { ethers, BigNumber } from 'ethers';
import { ExternalProvider } from '@ethersproject/providers';
import {
  AkashChain,
  BridgeTx,
  BridgeTxApiSingleResultStatus,
  CantoChain,
  CelestiaChain,
  CosmosChain,
  CosmosHubChain,
  CroChain,
  CronosChain,
  EthChainId,
  IrisChain,
  JunoChain,
  Network,
  Networks,
  SourceChain,
  TerraChain,
  TokenAsset,
  TokenAssetNetworkPair,
  TxChain,
  TxStatus,
  Wallet,
  WalletType,
} from '@types';
import staticConfig from '@config/static-config';
import ERC20_ABI from '@config/abi/erc20.json';
import CRC21_ABI from '@config/abi/crc21.json';
import { getChainInfo } from '@lib/cosmos-chain';
import { getCrc21AutoContract } from '@lib/gravity-bridge';
import { UnsupportedError } from '@errors';
import getWeb3Contract from '@config/static-config/contract';

const { networks, walletSupportedNetworks } = staticConfig;

export const promiseEach = async <T>(arr: T[], fn: (arg: T) => void) => {
  for (const item of arr) await fn(item);
};

export const numberToHex = (number: number) =>
  '0x' + number.toString(16).toUpperCase();

// For converting between eth & cosmos address

export const hexToBech32 = (hex: string, prefix: string = '') => {
  const hexWithout0x = hex.replace('0x', '');
  const buffer = Buffer.from(hexWithout0x, 'hex');
  const words = bech32.toWords(buffer);
  return bech32.encode(prefix, words);
};

export const bech32ToHex = (bech32String: string) => {
  const { words } = bech32.decode(bech32String);
  return '0x' + Buffer.from(bech32.fromWords(words)).toString('hex');
};

export const shortenAddress = (address: string) => {
  if (address.length <= 10) return address;
  return (
    address.substring(0, 6) +
    '...' +
    address.substring(address.length - 4, address.length)
  );
};

// For validating amount input to match precision

interface Options {
  isValid: boolean;
  amountAvailable: string;
}

export const restrictInput = (value: string, precision: number): string => {
  let input = value;

  // restriction 1: allow only digits and decimal character
  input = input.replace(/[^0-9.]/g, '');

  // case when user enter .1 it should be 0.1
  if (input[0] === '.') input = 0 + input;

  // restriction 2: user cannot enter more decimal places than set precision rule
  if (input.indexOf('.') > -1) {
    let integer = input.split('.')[0];
    let decimal = input.split('.')[1];
    if (decimal.length > precision) {
      decimal = decimal.substring(0, precision);
      input = integer + '.' + decimal;
    }
  }

  return input;
};

export const sanitizeInput = (rawInput: string, precision: number): string => {
  let value = rawInput;

  // case 1: first char is decimal, then add '0' before it
  if (value.charAt(0) === '.') {
    value = '0' + value;
  }

  // case 2: remove leading zero
  if (value.charAt(0) === '0' && value.charAt(1) != '.') {
    value = value.substring(1);
  }

  // case 3: multiple decimals, then keep the value before the second
  if (value.indexOf('.') > -1) {
    const strArr = value.split('.');
    value = strArr[0] + '.' + strArr[1];
  }

  // case 4: UI requirement
  value = roundDown(value, precision);

  return value;
};

export const validateAmount = (
  maxAmountAvailableFromWallet: string,
  inputAmount: string
): Options => {
  const maxAvailable = Number(
    getMaxAmountPrecise(maxAmountAvailableFromWallet, 5)
  );
  const options = {
    isValid: true,
    amountAvailable: maxAvailable.toString(),
  };

  if (Number(inputAmount) <= maxAvailable) {
    return options;
  }
  options.isValid = false;
  return options;
};

export const getMaxAmountPrecise = (
  maxAmountAvailableFromWallet: string,
  precision: number
): string => {
  return roundDown(maxAmountAvailableFromWallet, precision);
};

export const roundDown = (value: string, precision: number): string => {
  const number = Number(value);
  const decimal = new Decimal(number);
  const result = decimal
    .todp(precision, Decimal.ROUND_DOWN)
    .toFixed(precision)
    .toString();

  return parseFloat(result).toString();
};

// For validating wallet address based on destination network

export const isAddress = (toAddress: string, network: Network) => {
  const validAddressFor = {
    [Network.Cronos]: isCronosAddress(toAddress),
    [Network.CryptoOrg]: isCryptoOrgAddress(toAddress),
    [Network.Cosmos]: isCosmosAddress(toAddress),
    [Network.Terra]: isTerraAddress(toAddress),
    [Network.Juno]: isJunoAddress(toAddress),
    [Network.Akash]: isAkashAddress(toAddress),
    [Network.Canto]: isCantoAddress(toAddress),
    [Network.Iris]: isIrisAddress(toAddress),
    [Network.Celestia]: isCelestiaAddress(toAddress),
    [Network.Ethereum]: isEthAddress(toAddress),
  };

  return validAddressFor[network];
};

const isCryptoOrgAddress = (toAddress: string) => {
  return /^(cro|tcro)[0-9a-z]{39}$/i.test(toAddress);
};

const isCosmosAddress = (toAddress: string) => {
  return /^(cosmos)[0-9a-z]{39}$/i.test(toAddress);
};

const isTerraAddress = (toAddress: string) => {
  return /^(terra)[0-9a-z]{39}$/i.test(toAddress);
};

const isJunoAddress = (toAddress: string) => {
  return /^(juno)[0-9a-z]{39}$/i.test(toAddress);
};

const isAkashAddress = (toAddress: string) => {
  return /^(akash)[0-9a-z]{39}$/i.test(toAddress);
};

const isCantoAddress = (toAddress: string) => {
  return (
    web3.utils.isAddress(toAddress) || /^(canto)[0-9a-z]{39}$/i.test(toAddress)
  );
};

const isIrisAddress = (toAddress: string) => {
  return /^(iaa)[0-9a-z]{39}$/i.test(toAddress);
};

const isCelestiaAddress = (toAddress: string) => {
  return /^(celestia)[0-9a-z]{39}$/i.test(toAddress);
};

const isCronosAddress = (toAddress: string) => {
  return /^(0x)[0-9a-z]{40}$/i.test(toAddress);
};

const isEthAddress = (toAddress: string) => {
  return web3.utils.isAddress(toAddress);
};

export function isEthChain(network: Network): boolean {
  return network == Network.Ethereum || network == Network.Cronos;
}

export const isWalletSupportedByNetwork = (
  walletType: WalletType,
  network: Network,
  isTestnet: boolean
) =>
  walletSupportedNetworks[isTestnet ? 'testnet' : 'mainnet'][
    walletType
  ].includes(network);

export const getHasAddressInput = (
  fromNetwork: Network,
  toNetwork: Network,
  isTestnet: boolean
) => {
  const networksConfig = isTestnet ? networks.testnet : networks.mainnet;
  return !!networksConfig?.[fromNetwork]?.toNetworks[toNetwork]?.addressInput;
};

export function isAmountZeroEquivalent(amount: string): boolean {
  return amount.replace(/0/g, '').replace('.', '') === '';
}

// Utils for IBC transfer

export const tokenAssetToDenom = (
  tokenAsset: TokenAsset,
  chain: CosmosChain
): string => {
  switch (tokenAsset.symbol) {
    case 'CRO':
    case 'ATOM':
    case 'LUNA':
    case 'JUNO':
    case 'AKT':
    case 'CANTO':
    case 'IRIS':
    case 'TIA':
      return getChainInfo(chain).currencies[0].coinMinimalDenom;
    default:
      throw new UnsupportedError(tokenAsset.symbol);
  }
};

export const amountToBaseAmount = (
  amount: string | number,
  tokenAsset: TokenAsset
): number => {
  switch (tokenAsset.symbol) {
    case 'CRO':
    case 'ATOM':
    case 'LUNA':
    case 'JUNO':
    case 'AKT':
    case 'CANTO':
    case 'IRIS':
    case 'TIA': {
      // TODO: revisit and see if need BigNumber
      const amountNumber =
        typeof amount === 'string' ? parseFloat(amount) : amount;
      return Math.floor(amountNumber * Math.pow(10, tokenAsset.decimals));
    }
    default:
      throw new UnsupportedError(tokenAsset.symbol);
  }
};

export const minBalanceForSymbol = (symbol?: string): number => {
  let minBalance;
  switch (symbol) {
    case 'CRO':
      minBalance = 2.0;
      break;
    case 'ATOM':
      minBalance = 0.1;
      break;
    case 'LUNA':
      minBalance = 0.1;
      break;
    case 'JUNO':
      minBalance = 0.01;
      break;
    case 'AKT':
      minBalance = 0.3;
      break;
    case 'ETH':
      minBalance = 0.05;
      break;
    case 'CANTO':
      minBalance = 0.5;
      break;
    case 'IRIS':
      minBalance = 0.05;
      break;
    case 'TIA':
      minBalance = 0.02;
      break;
    default:
      minBalance = 0;
      break;
  }
  return minBalance;
};

// Utils for bridge transfer, converting amount to token decimal

export const generateSigner = async (
  wallet: Wallet
): Promise<ethers.Signer> => {
  const provider = wallet.eth?.currentProvider;
  return new ethers.providers.Web3Provider(
    provider as ExternalProvider
  ).getSigner();
};

export const convertAmountBN = (
  srcChain: Network,
  inputAmount: string,
  tokenAssetDecimals: number
) => {
  switch (srcChain) {
    case Network.Cronos:
      return ethers.utils.parseUnits(inputAmount, tokenAssetDecimals);
    case Network.Ethereum:
      return ethers.utils.parseUnits(inputAmount, tokenAssetDecimals);
    case Network.CryptoOrg:
      return ethers.utils.parseUnits(inputAmount, 8);
    default:
      throw new UnsupportedError(srcChain);
  }
};

export const isCronosChain = (chain: EthChainId): boolean => {
  return [
    EthChainId.Cronos,
    EthChainId.CronosTestnet,
    EthChainId.CronosGravityTestnet,
  ].includes(chain);
};

export const getNativeCurrencySymbolInEthereumNetworks = (
  chain: EthChainId
): string => {
  if (isCronosChain(chain)) {
    return 'CRO';
  } else {
    return 'ETH';
  }
};

export const getChainDisplayName = (
  chain: CosmosChain | EthChainId
): string => {
  if (Object.values(CosmosHubChain).includes(chain as CosmosHubChain)) {
    switch (chain) {
      case CosmosHubChain.mainnet:
        return 'Cosmos Mainnet';
      case CosmosHubChain.testnet:
        return 'Cosmos Testnet';
      default:
        return '';
    }
  }

  if (Object.values(TerraChain).includes(chain as TerraChain)) {
    switch (chain) {
      case TerraChain.mainnet:
        return 'Terra Mainnet';
      case TerraChain.testnet:
        return 'Terra Testnet';
      default:
        return '';
    }
  }

  if (Object.values(JunoChain).includes(chain as JunoChain)) {
    switch (chain) {
      case JunoChain.mainnet:
        return 'Juno Mainnet';
      case JunoChain.testnet:
        return 'Juno Testnet';
      default:
        return '';
    }
  }

  if (Object.values(AkashChain).includes(chain as AkashChain)) {
    switch (chain) {
      case AkashChain.mainnet:
        return 'Akash Mainnet';
      case AkashChain.testnet:
        return 'Akash Testnet';
      default:
        return '';
    }
  }

  if (Object.values(CantoChain).includes(chain as CantoChain)) {
    switch (chain) {
      case CantoChain.mainnet:
        return 'Canto Mainnet';
      case CantoChain.testnet:
        return 'Canto Testnet';
      default:
        return '';
    }
  }

  if (Object.values(IrisChain).includes(chain as IrisChain)) {
    switch (chain) {
      case IrisChain.mainnet:
        return 'Iris Mainnet';
      case IrisChain.testnet:
        return 'Iris Testnet';
      default:
        return '';
    }
  }

  if (Object.values(CelestiaChain).includes(chain as CelestiaChain)) {
    switch (chain) {
      case CelestiaChain.mainnet:
        return 'Celestia Mainnet';
      default:
        return '';
    }
  }

  if (Object.values(CroChain).includes(chain as CroChain)) {
    switch (chain) {
      case CroChain.mainnet:
        return 'Crypto.org Mainnet';
      case CroChain.testnet4:
        return 'Crypto.org Testnet4';
      default:
        return '';
    }
  }

  if (Object.values(EthChainId).includes(chain as EthChainId)) {
    return (
      Object.entries(EthChainId).find(([, value]) => value === chain)?.[0] || ''
    );
  }

  return '';
};

// For calculating gas margin adding 10%
export const calculateGasMargin = (value: number): string => {
  return BigNumber.from(value)
    .mul(BigNumber.from(10000).add(BigNumber.from(1000)))
    .div(BigNumber.from(10000))
    .toString();
};

export const getDisplaySymbol = (symbol: string) =>
  symbol === 'ETH' ? 'WETH' : symbol;

export const getDefaultNetworks = (networksConfig: Networks) => {
  const fromNetwork = Object.entries(networksConfig)
    .filter(([, { disabled }]) => !disabled)
    .map(([key]) => key)[0] as Network;
  const toNetwork = Object.entries(
    networksConfig[fromNetwork]?.toNetworks || {}
  )
    .filter(([, { disabled }]) => !disabled)
    .map(([key]) => key)[0] as Network;
  return { fromNetwork, toNetwork };
};

// Utils for retrieving token balance

export const getContractCronosEthereum = async (
  wallet: Wallet,
  tokenAsset: TokenAsset,
  isTestnet: boolean
) => {
  // TODO: in production, fix address should be provided for both eth and cronos
  if (wallet.chain in tokenAsset.address) {
    return tokenAsset.address[wallet.chain];
  }
  if (!isCronosChain(wallet.chain as EthChainId)) return;
  const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;
  return await getCrc21AutoContract(
    cronosChain,
    tokenAsset.address[isTestnet ? EthChainId.Goerli : EthChainId.Mainnet]
  );
};

export const getAbiCronosEthereum = (wallet: Wallet) => {
  if (isCronosChain(wallet.chain as EthChainId)) {
    return CRC21_ABI.abi;
  }
  return ERC20_ABI;
};

export const getNativeBalanceInEthNetwork = async (
  wallet: Wallet,
  walletAddress: string
) => {
  return await wallet.eth?.getBalance(walletAddress);
};

export const getContractBalanceInEthNetwork = async (
  wallet: Wallet,
  contractChain: string | undefined,
  abi: AbiItem[],
  walletAddress: string
) => {
  const contract = getWeb3Contract(
    abi as AbiItem[],
    contractChain,
    wallet.chain as EthChainId
  );
  return await contract.methods.balanceOf(walletAddress).call();
};

export const convertDepositTxStatus = (
  responseStatus: BridgeTxApiSingleResultStatus,
  destTxId: string | undefined | null
) => {
  if (destTxId) {
    return TxStatus.fulfilled;
  }

  switch (responseStatus) {
    case BridgeTxApiSingleResultStatus.counterpartyConfirmed:
    case BridgeTxApiSingleResultStatus.counterpartyRejected:
      return TxStatus.fulfilled;
    case BridgeTxApiSingleResultStatus.failed:
    case BridgeTxApiSingleResultStatus.cancelled:
      return TxStatus.failed;
    case BridgeTxApiSingleResultStatus.pending:
    default:
      return TxStatus.pending;
  }
};

export const convertTransferTxStatus = (
  responseStatus: BridgeTxApiSingleResultStatus
) => {
  switch (responseStatus) {
    case BridgeTxApiSingleResultStatus.counterpartyConfirmed:
      return TxStatus.fulfilled;
    case BridgeTxApiSingleResultStatus.failed:
    case BridgeTxApiSingleResultStatus.cancelled:
    case BridgeTxApiSingleResultStatus.counterpartyRejected:
      return TxStatus.failed;
    case BridgeTxApiSingleResultStatus.pending:
    default:
      return TxStatus.pending;
  }
};

export function getTokenAssetNetworkPair(
  fromNetwork: Network,
  toNetwork: Network
): TokenAssetNetworkPair {
  return `${fromNetwork}:${toNetwork}`;
}

export const getUrlWithBasePath = (url: string): string =>
  process.env.NEXT_PUBLIC_BASE_PATH + url;

export const getUrlWithBasePathOrPublicUrl = (url: string): string =>
  !url || url.startsWith('http') ? url : getUrlWithBasePath(url);

export const getUrlWithAbsolutePath = (url: string): string =>
  process.env.NEXT_PUBLIC_CRONOS_BRIDGE_BASE_URL + url;

export const getUrlWithAbsolutePathOrPublicUrl = (url: string): string =>
  !url || url.startsWith('http') ? url : getUrlWithAbsolutePath(url);

export const getChainFromBridgeTxApi = (
  network: SourceChain,
  isTestnet: boolean
): TxChain | null => {
  switch (network) {
    case SourceChain.cronos:
      return isTestnet ? CronosChain.testnet : CronosChain.mainnet;
    case SourceChain.cryptoOrg:
      return isTestnet ? CroChain.testnet4 : CroChain.mainnet;
    case SourceChain.ethereum:
      return isTestnet ? EthChainId.Goerli : EthChainId.Mainnet;
    default:
      return null;
  }
};

export const isBetaNetwork = (network: Network | undefined | null): boolean => {
  return (
    network === Network.Cosmos ||
    network === Network.Terra ||
    network === Network.Juno ||
    network === Network.Akash ||
    network === Network.Canto ||
    network === Network.Iris ||
    network === Network.Celestia ||
    network === Network.Ethereum
  );
};

// FIXME: network without confirmation of transfer
export const isBetaTx = (bridgeTx: BridgeTx | undefined | null): boolean => {
  const depositTx = bridgeTx?.depositTx;
  const transferTx = bridgeTx?.transferTx;
  return (
    isBetaNetwork(depositTx?.network) || isBetaNetwork(transferTx?.network)
  );
};

interface IsNativeTokenProps {
  network: Network;
  symbol: string;
}
export function IsNativeToken({ network, symbol }: IsNativeTokenProps) {
  const isCronosNativeToken = network === Network.Cronos && symbol === 'CRO';
  const isCryptoOrgNativeToken =
    network === Network.CryptoOrg && symbol === 'CRO';
  const isCosmosNativeToken = network === Network.Cosmos && symbol === 'ATOM';
  const isTerraNativeToken = network === Network.Terra && symbol === 'LUNA';
  const isJunoNativeToken = network === Network.Juno && symbol === 'JUNO';
  const isAkashNativeToken = network === Network.Akash && symbol === 'AKT';
  const isCantoNativeToken = network === Network.Canto && symbol === 'CANTO';
  const isIrisNativeToken = network === Network.Iris && symbol === 'IRIS';
  const isCelestiaNativeToken =
    network === Network.Celestia && symbol === 'TIA';
  const isEthereumNativeToken =
    network === Network.Ethereum && symbol === 'ETH';

  return (
    isCronosNativeToken ||
    isCryptoOrgNativeToken ||
    isCosmosNativeToken ||
    isTerraNativeToken ||
    isJunoNativeToken ||
    isAkashNativeToken ||
    isCantoNativeToken ||
    isIrisNativeToken ||
    isCelestiaNativeToken ||
    isEthereumNativeToken
  );
}
